import {
  ConversationListResponse,
  ConversationPageResponse,
  ConversationUnreadResponse,
} from "@edu/shared";
import { call, callVoid } from "../../lib/api.ts";

/**
 * Четыре ручки окна переписки. Живут здесь, а не в `lib/api.ts`, по той же
 * соседской причине, что панель «Где дыры» и справочник тем формы выдачи: схемы
 * нужны ровно этим экранам, а базовый адрес, cookie и разбор отказов приезжают
 * общие, через `call`.
 *
 * ⚠️ **Ключ разговора это СОСТАВНАЯ строка** (`person:<uuid>` либо
 * `group:<uuid>`), и собирает её `conversationId` из контрактов, а не
 * конкатенация по месту. Здесь ключ только подставляется в адрес, и подставляется
 * через `encodeURIComponent`: двоеточие в сегменте пути законно, но полагаться на
 * это в четырёх местах хуже, чем закодировать в одном.
 *
 * ⚠️ **Ни одна ручка не принимает и не отдаёт роль читателя.** Сторона
 * вычисляется сервером позиционно и приезжает полем `myRole`, а правила
 * видимости живут в `apps/api/src/repo/conversation-scope.ts`. Клиент их не
 * повторяет и повторять не должен: второе место, где решают, кто что видит,
 * однажды поправят не так.
 *
 * ⚠️ **Писать отсюда нельзя ничем, кроме отметки о прочтении.** Отправка это
 * `POST /v1/announcements` и `POST /v1/announcements/:id/replies` в соседнем
 * `announcements-api.ts`: второго способа написать в продукте не появилось.
 */

export function listConversations(): Promise<ConversationListResponse> {
  return call("/v1/conversations", {}, ConversationListResponse);
}

/**
 * Дешёвый счётчик: три числа на разговор и ни одного тела.
 *
 * ⚠️ Её зовёт опрос раз в пять секунд (`features/shell/ConversationNews.tsx`), и
 * это единственная ручка продукта, которую зовут по таймеру. Отсюда и лимит
 * частоты на сервере, и то, что она не возит ни имён, ни текстов.
 *
 * ⚠️ **`opened` это разговор, открытый прямо сейчас, и без него счётчик слепнет
 * на пятьдесят первом окне.** Строк приезжает не больше полусотни, самые свежие;
 * разговор за этой чертой не получал строки никогда, то есть открытое на нём
 * окно не узнавало о новом сообщении вовсе, хотя число у раздела его считало.
 * Сервер поднимает названный разговор в начало сортировки, а незнакомый ключ
 * просто ничего не двигает.
 */
export function getConversationUnread(opened?: string): Promise<ConversationUnreadResponse> {
  const q = opened === undefined ? "" : `?id=${encodeURIComponent(opened)}`;
  return call(`/v1/conversations/unread${q}`, {}, ConversationUnreadResponse);
}

/**
 * Страница переписки: свежие ПЕРВЫМИ, `cursor` уводит к более старым.
 *
 * ⚠️ Порядок переворачивает экран, а не эта функция: разворот это вопрос
 * отрисовки, а склейка страниц идёт в том порядке, в каком их отдал сервер
 * (разбор у `ConversationPageResponse` в контрактах).
 */
export function getConversationPage(
  conversationId: string,
  cursor?: string,
): Promise<ConversationPageResponse> {
  const q = cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  return call(
    `/v1/conversations/${encodeURIComponent(conversationId)}/messages${q}`,
    {},
    ConversationPageResponse,
  );
}

/**
 * Открыли окно.
 *
 * Двигает указатель «докуда дочитал» и гасит квитанции всех непрочитанных
 * объявлений этого разговора. Одним запросом на окно, а не одним на сообщение:
 * при живом опросе раз в пять секунд дюжина квитанций дюжиной запросов стоила бы
 * больше, чем само чтение.
 */
export function markConversationRead(conversationId: string): Promise<void> {
  return callVoid(`/v1/conversations/${encodeURIComponent(conversationId)}/read`, {
    method: "POST",
  });
}
